import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  TableHeaderContainer,
  TableHeader,
  TableRow,
  TableCell,
  PlayerInfo,
  PlayerAvatar,
  SearchInput,
  SearchWrapper,
  IndentedContent,
  PointsInput,
} from '../NormalDetails.styles';
import {
  IconButton,
  Collapse,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import Fuse from 'fuse.js';
import { transformData } from '../../utils';
import * as mutations from '../../../../../../graphql/mutations';
import { LOG } from 'application/common/Utilities';

interface Props {
  data: any[];
  managePoints: boolean;
  gameDimension: string;
  closeAndRefetch: (toastConfig: any) => void;
  isRefetching: boolean;
  isIncentiveGame?: boolean;
}

const getIncentiveMissionKey = (mission: any): string | undefined => {
  if (!mission) return undefined;
  return (
    mission?.userMissionID ||
    mission?.circleMissionID ||
    mission?.id ||
    mission?.missionID
  );
};

const getMissionActualValue = (mission: any): string => {
  if (!mission) return '0';
  const candidates = [
    mission.actual,
    mission.currentActionCount,
    mission.doneActionCount,
    mission.doneCountFormatted,
    mission.progressPercent,
    mission.points,
    mission.score,
  ];

  const found = candidates.find(
    (val) => val !== undefined && val !== null && val !== ''
  );

  return String(found ?? '0');
};

const buildIncentiveScoreMap = (items: any[] = []) => {
  return items.reduce((acc, item) => {
    (item?.missions || []).forEach((mission: any) => {
      const missionKey = getIncentiveMissionKey(mission);
      if (!missionKey) return;
      acc[missionKey] = getMissionActualValue(mission);
    });

    (item?.users || []).forEach((member: any) => {
      const memberKey = getIncentiveMissionKey(member);
      if (memberKey) {
        acc[memberKey] = getMissionActualValue(member);
      }

      (member?.kpis || []).forEach((kpi: any) => {
        const kpiWithKey = {
          ...kpi,
          userMissionID:
            kpi?.userMissionID ||
            kpi?.circleMissionID ||
            kpi?.missionID ||
            `${memberKey || member?.user?.id || ''}-${kpi?.id || ''}`,
        };
        const kpiKey = getIncentiveMissionKey(kpiWithKey);
        if (!kpiKey) return;
        acc[kpiKey] = getMissionActualValue(kpiWithKey);
      });
    });

    return acc;
  }, {} as Record<string, string>);
};

const sanitizeNumericInput = (value: string) => {
  if (typeof value !== 'string') return '';
  const cleaned = value.replace(/[^0-9.]/g, '');
  const parts = cleaned.split('.');
  if (parts.length <= 2) {
    return cleaned;
  }
  return `${parts[0]}.${parts.slice(1).join('')}`;
};

declare global {
  interface Window {
    appSettings?: {
      features?: {
        leader?: {
          hideProgressCircle?: boolean;
        };
      };
    };
  }
}

const GamesTable = ({
  data,
  managePoints,
  gameDimension,
  closeAndRefetch,
  isRefetching,
  isIncentiveGame = false,
}: Props) => {
  console.log('🚀 ~ isIncentiveGame:', isIncentiveGame);
  console.log('🚀 ~ data sample:', data?.[0]);
  console.log('🚀 ~ data sample missions:', data?.[0]?.missions);
  const navigate = useNavigate();
  const theme = useTheme();
  const isAdmin = Boolean(window.me?.isAdmin);
  const manageMode = managePoints && isAdmin;

  // Enhanced data logging for debugging
  useEffect(() => {
    // Check for team/circle structure in the data
    const hasNestedUsers = data?.some(
      (item) => item.users && item.users.length > 0
    );
    const profileTypesSet = new Set(
      data?.map((item) => item.profileType) || []
    );
    const profileTypes = Array.from(profileTypesSet);
    LOG.debug('GamesTable data analysis:', {
      count: data?.length || 0,
      hasNestedUsers,
      profileTypes,
      gameDimension,
      // Log sample item structure if available
      sampleItem:
        data?.length > 0
          ? {
              id: data[0].id,
              editID: data[0].editID,
              profileType: data[0].profileType,
              hasUsers: Boolean(data[0].users?.length),
              userCount: data[0].users?.length || 0,
            }
          : null,
    });
  }, [data, gameDimension]);

  const headers = ['Rk', 'Name', 'Score'];
  const [scoreMap, setScoreMap] = useState<Record<string, string>>({});
  const [scoreMapOriginals, setScoreMapOriginals] = useState<
    Record<string, string>
  >({});
  const [tableData, setTableData] = useState<any[]>([]);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [incentiveExpandedRows, setIncentiveExpandedRows] = useState<
    Record<string, boolean>
  >({});
  const [expandedUsers, setExpandedUsers] = useState<Record<string, boolean>>(
    {}
  );
  const [incentiveScoreMap, setIncentiveScoreMap] = useState<
    Record<string, string>
  >({});
  const [incentiveScoreOriginals, setIncentiveScoreOriginals] = useState<
    Record<string, string>
  >({});

  // Add debug log for tableData changes
  useEffect(() => {
    LOG.debug('Current tableData:', tableData);
  }, [tableData]);

  const getAvatarUrl = async (imageName: string) => {
    if (!imageName || imageName === '') return null;
    try {
      const url = await Storage.get(imageName);
      // Additional check to ensure we have a valid URL
      return url && url !== '' ? url : null;
    } catch (error) {
      console.error('Error getting avatar URL:', error);
      return null;
    }
  };

  const loadAvatarUrls = async (items: any[]) => {
    const urls: Record<string, string> = {};
    const promises = items.map(async (item) => {
      if (item.imageName) {
        const url = await getAvatarUrl(item.imageName);
        if (url) urls[item.id] = url;
      }
      if (item.users) {
        await Promise.all(
          item.users.map(async (member: any) => {
            if (member.user.imageName) {
              const url = await getAvatarUrl(member.user.imageName);
              if (url) urls[member.user.id] = url;
            }
          })
        );
      }
    });
    await Promise.all(promises);
    setAvatarUrls(urls);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    if (!data || !data.length) {
      setTableData([]);
      return;
    }

    if (searchTerm?.length) {
      const fuse = new Fuse(data, {
        keys: ['name'],
        threshold: 0.1,
      });

      const searchResults = fuse
        .search(searchTerm)
        .map((result) => result.item);
      setTableData(transformData(searchResults));
    } else {
      setTableData(transformData(data));
    }
  };

  useEffect(() => {
    const processData = async () => {
      const transformed = transformData(data || []);
      setTableData(transformed);
      if (isIncentiveGame) {
        const incentiveMap = buildIncentiveScoreMap(data || []);
        setIncentiveScoreMap(incentiveMap);
        setIncentiveScoreOriginals(incentiveMap);
      } else {
        setIncentiveScoreMap({});
        setIncentiveScoreOriginals({});
      }
      await loadAvatarUrls(data || []);
    };
    processData();
  }, [data, isIncentiveGame]);

  const handlePointsUpdate = async () => {
    if (!isAdmin) {
      setUpdating(false);
      closeAndRefetch({
        text1: 'You do not have permission to update scores.',
        type: 'error',
        autoHide: true,
        visibilityTime: 4000,
      });
      return;
    }

    setUpdating(true);
    let toastMessage = 'Scores successfully updated!';
    let messageType = 'success';

    const updateValues: Record<string, string> = {};
    if (!isIncentiveGame) {
      Object.entries(scoreMap).forEach(([key, val]) => {
        if (scoreMapOriginals[key] !== val) {
          updateValues[key] = val;
        }
      });
    }

    const missionUpdates: Record<string, string> = {};
    if (isIncentiveGame) {
      Object.entries(incentiveScoreMap).forEach(([key, val]) => {
        if (incentiveScoreOriginals[key] !== val) {
          missionUpdates[key] = val;
        }
      });
    }

    Object.assign(updateValues, missionUpdates);

    // Skip if no changes detected
    if (Object.keys(updateValues).length === 0) {
      setUpdating(false);
      closeAndRefetch({
        text1: 'No changes detected',
        type: 'info',
        autoHide: true,
        visibilityTime: 3000,
      });
      return;
    }

    try {
      const hasNestedUsers = data.some(
        (item) => item.users && item.users.length > 0
      );
      const entityTypeSet = new Set(
        data?.map((item) => item.profileType) || []
      );
      const entityTypes = Array.from(entityTypeSet);
      const hasPublicType = entityTypes.includes('public');

      let effectiveDimensionType = gameDimension;

      if (gameDimension === 'circle') {
        effectiveDimensionType = 'circle';
      } else if (hasNestedUsers) {
        effectiveDimensionType = 'circle';
      } else if (
        hasPublicType &&
        (gameDimension === 'mission' || !gameDimension)
      ) {
        effectiveDimensionType = 'mission';
      }

      LOG.debug('Score update details:', {
        dimensionType: effectiveDimensionType,
        hasNestedUsers,
        entityTypes,
        updateCount: Object.keys(updateValues).length,
        exampleUpdateID: Object.keys(updateValues)[0],
      });

      await API.graphql(
        graphqlOperation(mutations.updateGameScores, {
          dimensionType: effectiveDimensionType,
          newValues: JSON.stringify(JSON.stringify(updateValues)),
        })
      );

      const updatedData = data.map((item) => {
        const updatedMissions = (item.missions || []).map((mission: any) => {
          const missionKey = getIncentiveMissionKey(mission);
          if (missionKey && missionUpdates[missionKey] !== undefined) {
            const newActual = missionUpdates[missionKey];
            return {
              ...mission,
              actual: newActual,
              doneActionCount: newActual,
              doneCountFormatted: newActual,
              currentActionCount: newActual,
              points: newActual,
              score: newActual,
            };
          }
          return mission;
        });

        const updatedUsers = (item.users || []).map((member: any) => {
          const memberKey = getIncentiveMissionKey(member);
          if (memberKey && missionUpdates[memberKey] !== undefined) {
            const newActual = missionUpdates[memberKey];
            return {
              ...member,
              actual: newActual,
              doneActionCount: newActual,
              doneCountFormatted: newActual,
              currentActionCount: newActual,
              points: newActual,
              score: newActual,
            };
          }
          return member;
        });

        if (updateValues[item.editID]) {
          return {
            ...item,
            score: updateValues[item.editID],
            // Update doneCountFormatted as well for circle type entities
            ...(item.profileType === 'circle' ||
            item.scope === 'circle' ||
            hasNestedUsers
              ? { doneCountFormatted: updateValues[item.editID] }
              : {}),
            missions: updatedMissions,
            users: updatedUsers,
          };
        }
        if (missionUpdates && Object.keys(missionUpdates).length) {
          return {
            ...item,
            missions: updatedMissions,
            users: updatedUsers,
          };
        }
        return item;
      });

      setTableData(transformData(updatedData));
      setScoreMapOriginals({ ...scoreMap });
      if (isIncentiveGame) {
        setIncentiveScoreOriginals({ ...incentiveScoreMap });
      }
      setIsDirty(false);

      closeAndRefetch({
        text1: toastMessage,
        type: messageType,
        autoHide: true,
        visibilityTime: 5000,
        needsFullRefresh: true, // Always do a full refresh to be safe
      });
    } catch (err) {
      console.error('ERROR ---- > ', err);
      messageType = 'error';
      toastMessage = 'An error occurred while trying to update scores';
      closeAndRefetch({
        text1: toastMessage,
        type: messageType,
        autoHide: true,
        visibilityTime: 5000,
      });
    }
    setUpdating(false);
  };

  useEffect(() => {
    if (data && manageMode) {
      const transformedData = transformData(data);
      setTableData(transformedData);
      const initialScores: Record<string, string> = {};
      transformedData.forEach((row) => {
        if (row.score !== undefined) {
          initialScores[row.editID] = row.score.toString();
        }
      });
      setScoreMap(initialScores);
      setScoreMapOriginals(initialScores);
    }
  }, [data, managePoints]);

  useEffect(() => {
    if (!manageMode) {
      setIsDirty(false);
      setErrors(false);
      return;
    }

    const regularDirty =
      JSON.stringify(scoreMap) !== JSON.stringify(scoreMapOriginals);
    const incentiveDirty = isIncentiveGame
      ? JSON.stringify(incentiveScoreMap) !==
        JSON.stringify(incentiveScoreOriginals)
      : false;

    const regularErrors = Object.values(scoreMap).some((val) => val === '');
    const incentiveErrors = isIncentiveGame
      ? Object.values(incentiveScoreMap).some((val) => val === '')
      : false;

    setIsDirty(regularDirty || incentiveDirty);
    setErrors(regularErrors || incentiveErrors);
  }, [
    manageMode,
    scoreMap,
    scoreMapOriginals,
    incentiveScoreMap,
    incentiveScoreOriginals,
    isIncentiveGame,
  ]);

  useEffect(() => {
    if (!manageMode && isIncentiveGame) {
      setIncentiveScoreMap({ ...incentiveScoreOriginals });
    }
  }, [manageMode, isIncentiveGame, incentiveScoreOriginals]);

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleIncentiveRow = (id: string) => {
    setIncentiveExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleUserRow = (userId: string) => {
    setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const calculateIncentiveResult = (missions: any[] = []) => {
    if (!Array.isArray(missions) || !missions.length) {
      return { result: '', hasReachedGoal: false };
    }

    let incrementCount = 0;
    for (const mission of missions) {
      if (mission.actual >= mission.goal) {
        incrementCount++;
      }
    }

    const hasReachedGoal = incrementCount >= missions.length;
    const result = `${incrementCount}/${missions.length}`;
    return { result, hasReachedGoal };
  };

  const handleRowClick = (row: any) => {
    if (gameDimension === 'circle') {
      navigate(`/teams/${row.id}`);
    } else {
      navigate(`/people/about/${row.id}`);
    }
  };

  const handleMemberClick = (member: any) => {
    navigate(`/people/about/${member.user.id}`);
  };

  const renderMemberRow = (member: any) => (
    <React.Fragment key={member.user.id}>
      <TableRow isEven={false} isNested={true}>
        <TableCell>{member.sequence}</TableCell>
        <TableCell>
          <PlayerInfo>
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleMemberClick(member);
              }}
              style={{ cursor: 'pointer' }}
            >
              <PlayerAvatar
                alt={member.user.name}
                src={avatarUrls[member.user.id]}
              >
                {(!avatarUrls[member.user.id] ||
                  avatarUrls[member.user.id] === '') &&
                  member.user.initials}
              </PlayerAvatar>
            </div>
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleMemberClick(member);
              }}
              style={{ cursor: 'pointer' }}
            >
              {member.user.name}
            </span>
          </PlayerInfo>
        </TableCell>
        <TableCell>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            {managePoints && isIncentiveGame && member ? (
              <PointsInput
                type="number"
                variant="outlined"
                value={(() => {
                  const key = getIncentiveMissionKey(member);
                  if (!key) return getMissionActualValue(member);
                  return (
                    incentiveScoreMap[key] ?? getMissionActualValue(member)
                  );
                })()}
                onChange={(e) => {
                  const key = getIncentiveMissionKey(member);
                  if (!key) return;
                  const sanitized = sanitizeNumericInput(e.target.value);
                  setIncentiveScoreMap((prev) => ({
                    ...prev,
                    [key]: sanitized,
                  }));
                }}
                error={(() => {
                  const key = getIncentiveMissionKey(member);
                  if (!key) return false;
                  const value =
                    incentiveScoreMap[key] ?? getMissionActualValue(member);
                  return value === '' || value == null;
                })()}
                size="small"
              />
            ) : (
              <span style={{ marginLeft: '16px' }}>
                {member.actual || '0'} / {member.goal}
              </span>
            )}
            {member.kpis?.length > 0 ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleUserRow(member.user.id);
                }}
                sx={{ color: theme.custom.textLight }}
              >
                {expandedUsers[member.user.id] ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            ) : (
              <IconButton size="small" style={{ visibility: 'hidden' }}>
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        </TableCell>
      </TableRow>
      {member.kpis?.length > 0 && (
        <Collapse
          in={expandedUsers[member.user.id]}
          timeout="auto"
          unmountOnExit
        >
          <IndentedContent>
            {member.kpis.map((kpi: any, kpiIndex: number) => (
              <TableRow
                key={`user-kpi-${member.user.id}-${kpiIndex}`}
                isEven={false}
                isNested={true}
              >
                <TableCell></TableCell>
                <TableCell>
                  <Box
                    sx={{
                      paddingLeft: '60px',
                      fontSize: '12px',
                      color: theme.custom.textLight,
                    }}
                  >
                    {kpi.name}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box
                    sx={{
                      color:
                        kpi.points >= kpi.goal && kpi.goal > 0
                          ? '#0C947E'
                          : theme.custom.text,
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textAlign: 'right',
                      paddingRight: '16px',
                    }}
                  >
                    {kpi.points || 0}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </IndentedContent>
        </Collapse>
      )}
    </React.Fragment>
  );

  const renderKPIRow = (kpi: any, kpiIndex: number) => {
    const missionKey = getIncentiveMissionKey(kpi);
    const actualValue = missionKey
      ? incentiveScoreMap[missionKey] ?? getMissionActualValue(kpi)
      : getMissionActualValue(kpi);
    const goalValue = kpi.goal;
    const isGoalReached = Number(actualValue) >= Number(goalValue);
    const scoreTextColor = isGoalReached ? '#0C947E' : theme.custom.text;

    return (
      <TableRow key={`kpi-${kpiIndex}`} isEven={false} isNested={true}>
        <TableCell></TableCell>
        <TableCell>
          <Box
            sx={{
              paddingLeft: '30px',
              fontSize: '12px',
              color: theme.custom.textLight,
            }}
          >
            {kpi.KPI}
          </Box>
        </TableCell>
        <TableCell>
          {managePoints && isIncentiveGame && missionKey ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <PointsInput
                type="number"
                variant="outlined"
                value={actualValue || ''}
                onChange={(e) => {
                  const sanitized = sanitizeNumericInput(e.target.value);
                  setIncentiveScoreMap((prev) => ({
                    ...prev,
                    [missionKey]: sanitized,
                  }));
                }}
                error={actualValue === '' || actualValue == null}
                size="small"
              />
              <Box
                component="span"
                sx={{
                  color: theme.custom.textLight,
                  fontSize: '12px',
                  fontWeight: 'bold',
                  paddingRight: '16px',
                }}
              >
                / {goalValue}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                color: scoreTextColor,
                fontSize: '12px',
                fontWeight: 'bold',
                textAlign: 'right',
                paddingRight: '16px',
              }}
            >
              {actualValue}/{goalValue}
            </Box>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderScoreCellContent = (row: any) => {
    if (manageMode && !isIncentiveGame) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <PointsInput
            type="number"
            variant="outlined"
            value={scoreMap[row.editID] || ''}
            onChange={(e) => {
              const sanitized = sanitizeNumericInput(e.target.value);
              setScoreMap((prev) => ({
                ...prev,
                [row.editID]: sanitized,
              }));
            }}
            error={scoreMap[row.editID] === ''}
            size="small"
          />
          {isIncentiveGame && row.missions?.length > 0 ? (
            <IconButton
              size="small"
              onClick={() => toggleIncentiveRow(row.id)}
              sx={{ color: theme.custom.textLight }}
            >
              {incentiveExpandedRows[row.id] ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          ) : row.users?.length > 0 ? (
            <IconButton
              size="small"
              onClick={() => toggleRow(row.id)}
              sx={{ color: theme.custom.textLight }}
            >
              {expandedRows[row.id] ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <IconButton size="small" style={{ visibility: 'hidden' }}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </div>
      );
    }

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <span style={{ marginLeft: '16px' }}>
          {isIncentiveGame && row.missions
            ? (() => {
                const { result, hasReachedGoal } = calculateIncentiveResult(
                  row.missions
                );
                return (
                  <span
                    style={{
                      color: hasReachedGoal ? '#0C947E' : theme.custom.text,
                    }}
                  >
                    {result}
                  </span>
                );
              })()
            : row.score}
        </span>
        {isIncentiveGame && row.missions?.length > 0 ? (
          <IconButton
            size="small"
            onClick={() => toggleIncentiveRow(row.id)}
            sx={{ color: theme.custom.textLight }}
          >
            {incentiveExpandedRows[row.id] ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        ) : row.users?.length > 0 ? (
          <IconButton
            size="small"
            onClick={() => toggleRow(row.id)}
            sx={{ color: theme.custom.textLight }}
          >
            {expandedRows[row.id] ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        ) : (
          <IconButton size="small" style={{ visibility: 'hidden' }}>
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </div>
    );
  };

  return (
    <>
      {isRefetching && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            position: 'relative',
          }}
        >
          <CircularProgress size={24} />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: `${theme.custom.background1}CC`,
              padding: '10px',
              borderRadius: '4px',
              color: theme.custom.text,
            }}
          >
            Updating data...
          </Box>
        </Box>
      )}
      <SearchWrapper>
        <SearchIcon fontSize="small" sx={{ color: theme.custom.textLight }} />
        <SearchInput placeholder="Search..." onChange={handleSearch} />
      </SearchWrapper>

      <TableHeaderContainer>
        {headers.map((header) => (
          <TableHeader key={header}>{header}</TableHeader>
        ))}
      </TableHeaderContainer>

      {tableData.map((row, index) => (
        <React.Fragment key={row.id}>
          <TableRow
            isEven={index % 2 === 0}
            isNested={false}
            style={{ cursor: 'pointer' }}
          >
            <TableCell>{row.position}</TableCell>
            <TableCell>
              <PlayerInfo>
                <PlayerAvatar
                  onClick={() => handleRowClick(row)}
                  alt={row.name}
                  src={avatarUrls[row.id]}
                >
                  {(!avatarUrls[row.id] || avatarUrls[row.id] === '') &&
                    row.initials}
                </PlayerAvatar>
                {row.name}
              </PlayerInfo>
            </TableCell>
            <TableCell>{renderScoreCellContent(row)}</TableCell>
          </TableRow>
          {isIncentiveGame && row.missions?.length > 0 && (
            <Collapse
              in={incentiveExpandedRows[row.id]}
              timeout="auto"
              unmountOnExit
            >
              <IndentedContent>
                {row.missions.map((kpi: any, kpiIndex: number) =>
                  renderKPIRow(kpi, kpiIndex)
                )}
              </IndentedContent>
            </Collapse>
          )}
          {row.users?.length > 0 && (
            <Collapse in={expandedRows[row.id]} timeout="auto" unmountOnExit>
              <IndentedContent>
                {row.users.map((member: any) => renderMemberRow(member))}
              </IndentedContent>
            </Collapse>
          )}
        </React.Fragment>
      ))}

      {isDirty && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '16px',
            width: '100%',
          }}
        >
          <Button
            variant="contained"
            onClick={handlePointsUpdate}
            disabled={updating || errors}
            sx={{
              backgroundColor: theme.custom.primary,
              '&:hover': {
                backgroundColor: theme.custom.primaryDark,
              },
              minWidth: '120px',
            }}
          >
            {updating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </>
  );
};

export default GamesTable;
